import React from 'react';
import { OnChangeValue } from 'react-select';
import { DefaultOption } from 'src/components/molecules';
import {
  getStrippedHtmlLength,
  MAX_DATA_GRID_COLUMNS,
  MAX_OPTIONS,
  MIN_OPTIONS,
  validateOptionText,
  validateQuestionDescription,
  validateQuestionText,
} from '../../../helpers/validation';
import { DEFAULT_FORM_EDITING_LOCALE } from '../../../helpers/localizedQuestionFieldHelpers';
import {
  ANSWER_TYPES,
  OPTION_BASED_TYPES,
  QuestionData,
  QuestionOption,
} from '../../../types';
import type { QuestionTranslationData } from 'src/types/formQuestionnaire';

const OTHER_OPTION_TEXT = 'Other (optional)';
const OTHER_OPTION_HELPER_TEXT =
  'When the user selects "Other," a text field appears where they can enter their own answer.';
const TYPES_WITH_OTHER_OPTION = new Set<string>([
  ANSWER_TYPES.RADIO,
  ANSWER_TYPES.CHECKBOX,
]);

const createOtherOption = (timestamp?: number): QuestionOption => ({
  id: `option-${timestamp || Date.now()}`,
  text: OTHER_OPTION_TEXT,
  helperText: OTHER_OPTION_HELPER_TEXT,
  textError: null,
  isOtherOption: true,
});

export const defaultOptionsConfig = {
  title: 'Answer options',
  addButtonText: 'Add option',
  emptyText: 'No options added yet',
  addFirstButtonText: 'Add option',
  placeholder: 'Enter option text',
};

export const answerTypeConfig: Record<
  string,
  {
    title: string;
    addButtonText: string;
    emptyText: string;
    addFirstButtonText: string;
    placeholder: string;
  }
> = {
  [ANSWER_TYPES.RADIO]: defaultOptionsConfig,
  [ANSWER_TYPES.CHECKBOX]: defaultOptionsConfig,
  [ANSWER_TYPES.DROPDOWN_SINGLE]: defaultOptionsConfig,
  [ANSWER_TYPES.DROPDOWN_MULTI]: defaultOptionsConfig,
  [ANSWER_TYPES.DATA_GRID]: {
    title: 'Data Grid columns',
    addButtonText: 'Add Column',
    emptyText: 'No columns added yet',
    addFirstButtonText: 'Add column',
    placeholder: 'Enter column header',
  },
};

export const DATABASE_FIELD_PREVIEW_DATA: Record<
  string,
  Array<{
    label: string;
    value: string;
    role?: string;
    department?: string;
    country?: string;
  }>
> = {
  employee_directory: [
    {
      label: 'Michael Schott',
      value: 'michael_schott',
      role: 'Regional Manager',
      department: 'Sales',
      country: 'United States',
    },
    {
      label: 'Jane Doe',
      value: 'jane_doe',
      role: 'HR Business Partner',
      department: 'Human Resources',
      country: 'United Kingdom',
    },
    {
      label: 'Ryan Howard',
      value: 'ryan_howard',
      role: 'VP of Sales',
      department: 'Sales',
      country: 'United States',
    },
    {
      label: 'Jo Benett',
      value: 'jo_benett',
      role: 'Accountant',
      department: 'Finance',
      country: 'Canada',
    },
  ],
};

export const createQuestionHandlers = (
  question: QuestionData,
  onChange: (id: string, data: Partial<QuestionData>) => void,
  onDelete: (id: string) => void,
  onDuplicate: (id: string) => void,
) => {
  const handleTextChange = (value: string) => {
    onChange(question.id, { questionText: value, textTouched: true });
  };

  const handleDescriptionChange = (value: string) => {
    onChange(question.id, { description: value, descriptionTouched: true });
  };

  const handleAnswerTypeChange = (
    option: OnChangeValue<DefaultOption, false>,
  ) => {
    if (option && 'value' in option) {
      const newQuestionType = String(option.value);
      const currentQuestionType = question.questionType || '';

      const isOptionBased = (type: string) => OPTION_BASED_TYPES.has(type);

      const shouldClearOptions =
        (isOptionBased(currentQuestionType) &&
          !isOptionBased(newQuestionType)) ||
        (!isOptionBased(currentQuestionType) && isOptionBased(newQuestionType));

      const updates: Partial<QuestionData> = {
        questionType: newQuestionType,
        answerTypeTouched: true,
        ...(shouldClearOptions && { options: undefined }),
      };

      // Track if "Other" option exists before removing it
      const hadOtherOption =
        TYPES_WITH_OTHER_OPTION.has(currentQuestionType) &&
        (question.options?.some((opt) => opt.isOtherOption) ?? false);

      // Remove "Other" option when changing to option-based types that do not support it
      if (
        TYPES_WITH_OTHER_OPTION.has(currentQuestionType) &&
        !TYPES_WITH_OTHER_OPTION.has(newQuestionType) &&
        isOptionBased(newQuestionType) &&
        question.options &&
        hadOtherOption
      ) {
        updates.options = question.options.filter((opt) => !opt.isOtherOption);
        // Store that this question had "Other" option
        updates.hadOtherOption = true;
      }

      // Re-add "Other" option when changing to a supported type, only if it existed before
      if (
        TYPES_WITH_OTHER_OPTION.has(newQuestionType) &&
        !TYPES_WITH_OTHER_OPTION.has(currentQuestionType) &&
        isOptionBased(currentQuestionType) &&
        question.options &&
        !question.options.some((opt) => opt.isOtherOption) &&
        question.hadOtherOption // Only re-add if it existed before
      ) {
        updates.options = [...question.options, createOtherOption()];
        // Clear the flag after re-adding
        updates.hadOtherOption = false;
      }

      if (
        newQuestionType === ANSWER_TYPES.DATE_PICKER ||
        newQuestionType === ANSWER_TYPES.TIME_PICKER
      ) {
        updates.dateTimeOptions = {
          datePicker: newQuestionType === ANSWER_TYPES.DATE_PICKER,
          timePicker: newQuestionType === ANSWER_TYPES.TIME_PICKER,
        };
      } else if (
        currentQuestionType === ANSWER_TYPES.DATE_PICKER ||
        currentQuestionType === ANSWER_TYPES.TIME_PICKER
      ) {
        updates.dateTimeOptions = undefined;
      }

      if (newQuestionType === ANSWER_TYPES.DATABASE_FIELD) {
        updates.databaseFieldOptions = {
          selectedFieldId: undefined,
          selectedFieldKey: undefined,
          allowMultiSelect: false,
        };
      } else if (currentQuestionType === ANSWER_TYPES.DATABASE_FIELD) {
        updates.databaseFieldOptions = undefined;
      }

      // No sync needed - all question types support independent branching per option

      onChange(question.id, updates);
    }
  };

  const handleRequiredChange = (value: boolean) => {
    onChange(question.id, { isRequired: value });
  };

  const handleFlagIfAnsweredChange = () => {
    onChange(question.id, { flagIfAnswered: !question.flagIfAnswered });
  };

  const handleDatabaseFieldChange = (
    value: OnChangeValue<DefaultOption, false>,
  ) => {
    const currentOptions = question.databaseFieldOptions || {
      allowMultiSelect: false,
    };
    if (value && 'value' in value) {
      const selectedOption = value as DefaultOption & { subTypeKey?: string };
      onChange(question.id, {
        databaseFieldOptions: {
          ...currentOptions,
          selectedFieldId: String(value.value),
          selectedFieldKey: selectedOption.subTypeKey,
        },
        databaseFieldTouched: true,
      });
    }
  };

  const handleDatabaseMultiSelectToggle = () => {
    const currentOptions = question.databaseFieldOptions || {
      allowMultiSelect: false,
    };
    onChange(question.id, {
      databaseFieldOptions: {
        ...currentOptions,
        allowMultiSelect: !currentOptions.allowMultiSelect,
      },
    });
  };

  const handleDeleteClick = () => {
    onDelete(question.id);
  };

  const handleDuplicateClick = () => {
    onDuplicate(question.id);
  };

  const handleToggleExpand = () => {
    const isExpanded =
      question.isExpanded !== undefined ? question.isExpanded : true;
    onChange(question.id, { isExpanded: !isExpanded });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleExpand();
    }
  };

  const handleAddFirstOption = () => {
    const baseTimestamp = Date.now();
    const newOptions: QuestionOption[] = TYPES_WITH_OTHER_OPTION.has(
      question.questionType || '',
    )
      ? [
          { id: `option-${baseTimestamp}`, text: '', textError: null },
          { id: `option-${baseTimestamp + 1}`, text: '', textError: null },
          createOtherOption(baseTimestamp + 2),
        ]
      : [
          { id: `option-${baseTimestamp}`, text: '', textError: null },
          { id: `option-${baseTimestamp + 1}`, text: '', textError: null },
        ];
    onChange(question.id, { options: newOptions, optionsError: null });
  };

  const handleAddOption = () => {
    const currentOptions = question.options || [];
    const maxOptionsForType =
      question.questionType === ANSWER_TYPES.DATA_GRID
        ? MAX_DATA_GRID_COLUMNS
        : MAX_OPTIONS;
    if (currentOptions.length >= maxOptionsForType) {
      return;
    }
    const newOption: QuestionOption = {
      id: `option-${Date.now()}`,
      text: '',
      textError: null,
    };

    // Insert new option before "Other" option if it exists
    const otherOptionIndex = currentOptions.findIndex(
      (opt) => opt.isOtherOption,
    );
    if (otherOptionIndex !== -1) {
      const updatedOptions = [
        ...currentOptions.slice(0, otherOptionIndex),
        newOption,
        ...currentOptions.slice(otherOptionIndex),
      ];
      onChange(question.id, { options: updatedOptions });
    } else {
      // No "Other" option, append to end
      onChange(question.id, { options: [...currentOptions, newOption] });
    }
  };

  const handleOptionTextChange = (optionId: string, text: string) => {
    const fieldName =
      question.questionType === ANSWER_TYPES.DATA_GRID
        ? 'Column header'
        : 'Option text';

    // Update the text for the edited option (skip if it's "Other" option)
    const updatedOptions = question.options?.map((opt) =>
      opt.id === optionId && !opt.isOtherOption ? { ...opt, text } : opt,
    );

    // Re-validate ALL options
    const finalOptions = updatedOptions?.map((opt) => {
      // Skip validation for "Other" option - it's system-controlled and always empty
      if (opt.isOtherOption) {
        return { ...opt, textError: null };
      }

      // Only validate if this option has content or if it's the one being edited
      const shouldValidate = opt.text.trim() !== '' || opt.id === optionId;

      if (!shouldValidate) {
        // Preserve existing errors on other empty options
        return opt;
      }

      const basicError = validateOptionText(opt.text, fieldName);
      if (basicError) {
        return { ...opt, textError: basicError };
      }

      // Check for duplicates
      const trimmedText = opt.text.trim().toLowerCase();
      const hasDuplicate =
        trimmedText &&
        updatedOptions.some(
          (other) =>
            other.id !== opt.id &&
            other.text.trim().toLowerCase() === trimmedText,
        );

      if (!hasDuplicate) {
        // No duplicate - clear any existing error
        return { ...opt, textError: null };
      }

      // Has duplicate - mark the edited option or preserve existing error
      const shouldMarkError =
        opt.id === optionId ||
        (opt.textError && opt.textError.includes('must be unique'));

      return {
        ...opt,
        textError: shouldMarkError ? `${fieldName} must be unique` : null,
      };
    });

    onChange(question.id, { options: finalOptions });
  };

  const handleDeleteOption = (optionId: string) => {
    const currentOptions = question.options || [];

    // Check if we're trying to delete the "Other" option
    const optionToDelete = currentOptions.find((opt) => opt.id === optionId);
    const isOtherOption = optionToDelete?.isOtherOption;

    // If not deleting "Other", check minimum regular options
    if (!isOtherOption) {
      const regularOptionsCount = currentOptions.filter(
        (opt) => !opt.isOtherOption,
      ).length;
      if (regularOptionsCount <= MIN_OPTIONS) {
        return;
      }
    }

    // If deleting "Other", allow it even if at minimum
    // Regular options still need minimum of 2
    const fieldName =
      question.questionType === ANSWER_TYPES.DATA_GRID
        ? 'Column header'
        : 'Option text';

    // Remove the option
    const remainingOptions = currentOptions.filter(
      (opt) => opt.id !== optionId,
    );

    // Re-validate all remaining options to clear stale duplicate errors
    const revalidatedOptions = remainingOptions.map(
      (opt, index, allOptions) => {
        // Skip validation for "Other" option - it's system-controlled
        if (opt.isOtherOption) {
          return { ...opt, textError: null };
        }

        // Don't validate empty options
        if (opt.text.trim() === '') {
          return { ...opt, textError: null };
        }

        const basicError = validateOptionText(opt.text, fieldName);
        if (basicError) {
          return { ...opt, textError: basicError };
        }

        // Check if this option is still a duplicate
        const trimmedText = opt.text.trim().toLowerCase();
        const hasDuplicate =
          trimmedText &&
          allOptions.some(
            (other, otherIndex) =>
              otherIndex < index &&
              other.text.trim().toLowerCase() === trimmedText,
          );

        return {
          ...opt,
          textError: hasDuplicate ? `${fieldName} must be unique` : null,
        };
      },
    );

    onChange(question.id, { options: revalidatedOptions });
  };

  const handleToggleOptionFlagged = (optionId: string) => {
    const updatedOptions = question.options?.map((opt) =>
      opt.id === optionId ? { ...opt, isFlagged: !opt.isFlagged } : opt,
    );
    onChange(question.id, { options: updatedOptions });
  };

  const handleNextActionChange = (
    optionId: string,
    value: OnChangeValue<DefaultOption, false>,
  ) => {
    if (value && 'value' in value) {
      const newNextAction = String(value.value);

      // Update only the selected option's branching
      const updatedOptions = question.options?.map((opt) =>
        opt.id === optionId ? { ...opt, nextAction: newNextAction } : opt,
      );

      onChange(question.id, { options: updatedOptions });
    }
  };

  return {
    handleTextChange,
    handleDescriptionChange,
    handleAnswerTypeChange,
    handleRequiredChange,
    handleFlagIfAnsweredChange,
    handleDatabaseFieldChange,
    handleDatabaseMultiSelectToggle,
    handleDeleteClick,
    handleDuplicateClick,
    handleToggleExpand,
    handleKeyDown,
    handleAddFirstOption,
    handleAddOption,
    handleOptionTextChange,
    handleDeleteOption,
    handleToggleOptionFlagged,
    handleNextActionChange,
  };
};

export const getQuestionTextError = (
  question: QuestionData,
  effectiveQuestionText: string,
  isSpeakUp: boolean,
  editingLocale: string | undefined,
  translationRowForLocale: QuestionTranslationData | undefined,
): string | null => {
  if (
    isSpeakUp &&
    editingLocale &&
    editingLocale !== DEFAULT_FORM_EDITING_LOCALE &&
    translationRowForLocale?.uiPublishQuestionTextError
  ) {
    return translationRowForLocale.uiPublishQuestionTextError;
  }
  return question.textTouched
    ? validateQuestionText(effectiveQuestionText, !isSpeakUp)
    : null;
};

export const getQuestionDescriptionError = (
  question: QuestionData,
  effectiveDescription: string,
  isSpeakUp: boolean,
  editingLocale: string | undefined,
  translationRowForLocale: QuestionTranslationData | undefined,
): string | null => {
  if (
    isSpeakUp &&
    editingLocale &&
    editingLocale !== DEFAULT_FORM_EDITING_LOCALE &&
    translationRowForLocale?.uiPublishDescriptionError
  ) {
    return translationRowForLocale.uiPublishDescriptionError;
  }
  return question.descriptionTouched
    ? validateQuestionDescription(effectiveDescription)
    : null;
};

export const getDescriptionCharCount = (description: string): number => {
  return getStrippedHtmlLength(description);
};

export const getDatabaseFieldError = (
  question: QuestionData,
): string | null => {
  if (question.questionType !== ANSWER_TYPES.DATABASE_FIELD) return null;
  if (!question.databaseFieldTouched) return null;
  if (!question.databaseFieldOptions?.selectedFieldId) {
    return 'Please select a database field';
  }
  return null;
};

export const getEffectiveOptionsError = (
  question: QuestionData,
  isSpeakUp: boolean,
  editingLocale: string | undefined,
  translationRowForLocale: QuestionTranslationData | undefined,
): string | null => {
  if (
    isSpeakUp &&
    editingLocale &&
    editingLocale !== DEFAULT_FORM_EDITING_LOCALE &&
    translationRowForLocale?.uiPublishOptionsError
  ) {
    return translationRowForLocale.uiPublishOptionsError;
  }
  return question.optionsError ?? null;
};
